/**
 * Sportscraft Stockist Accordion & Layout
 * ---------------------------------------------------------------------
 * Converts Stockist store locator results into accordions and
 * restructures the DOM to match the Sportscraft design.
 * Reaches into the widget's open shadowRoot to restructure the DOM AND to
 * inject sportscraft-stockist-app.css into it.
 *
 * The stylesheet injection (2026-09-04, Bec) replaces the previous delivery
 * route, which was a human pasting that file into the Stockist dashboard's
 * custom CSS field. That route drifted: the repo carried the Sportscraft
 * restyle from 2026-08-18 while the widget still rendered JAG's Lato styling,
 * because nobody had pasted it. Shipping the CSS with the theme makes the repo
 * copy the live one and removes the manual step.
 *
 * REQUIRED ONE-OFF: the Stockist dashboard's custom CSS field must be emptied,
 * or both copies apply at once.
 *
 * Also drives the NSW/VIC/QLD/NZ store filter menu rendered by the
 * sections/sportscraft-store-locator-header.liquid section elsewhere on the
 * store locator page (ported from SABA, fix/495-store-locator-boutique-pages):
 * filter clicks are delegated on `document` here, since this is the only
 * script with a handle on the widget's shadow root. Selecting a region runs
 * the widget's own address search (there's no direct handle on its internal
 * map instance) so the map recentres/zooms the same way it would for a
 * manual search, then hides any remaining non-matching rows client-side by
 * the state parsed from each result's address. The hidden-result class this
 * applies (sportscraft-store-hidden) is defined in
 * sportscraft-stockist-app.css, which ships with the theme via the injector
 * above.
 *
 * Conditionally loaded via page metafield:
 * custom.include_stockist_app_custom_styles_logic
 * ---------------------------------------------------------------------
 */

(function () {
  const STOCKIST_SELECTOR = 'stockist-store-locator';
  const MAX_POLL_ATTEMPTS = 100;
  const STATE_REGEX = /\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\b/i;
  /* NZ is filtered by country rather than by state, because New Zealand
     results carry a region (Auckland, Canterbury, ...) in the state field,
     not a value matching the filter button. */
  const NZ_COUNTRY_VALUES = ['NZ', 'NEW ZEALAND'];
  /* Covers all 8 AU states/territories plus New Zealand (matching the
     "State" block's select options in
     sections/sportscraft-store-locator-header.liquid) so a merchant can add
     a new filter button from the Theme Editor without a code change. */
  const REGION_SEARCH_QUERY = {
    NSW: 'New South Wales, Australia',
    VIC: 'Victoria, Australia',
    QLD: 'Queensland, Australia',
    WA: 'Western Australia, Australia',
    SA: 'South Australia, Australia',
    TAS: 'Tasmania, Australia',
    ACT: 'Australian Capital Territory, Australia',
    NT: 'Northern Territory, Australia',
    NZ: 'New Zealand',
  };
  let activeRegion = null;
  /* Captured at execution, before any async work -- document.currentScript is
     null inside callbacks. The layout passes the asset URL because Liquid
     filters cannot run inside a .js asset. */
  const SCRIPT_EL = document.currentScript;
  const STYLESHEET_URL = SCRIPT_EL ? SCRIPT_EL.getAttribute('data-stockist-css') : null;

  /**
   * Injects the theme's widget stylesheet into the open shadow root. Appended
   * rather than prepended so it lands after the app's own styles and wins at
   * equal specificity. Idempotent -- the widget re-renders on every search.
   */
  function injectStyles(shadowRoot) {
    if (!STYLESHEET_URL) return;
    if (shadowRoot.querySelector('link[data-sportscraft-stockist]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = STYLESHEET_URL;
    link.setAttribute('data-sportscraft-stockist', '');
    shadowRoot.appendChild(link);
  }

  /**
   * Separate, faster poll than waitForWidget: styles go in as soon as the
   * shadow root exists, rather than waiting for results to render, so the
   * widget never paints unstyled first.
   */
  function waitForShadowRoot() {
    let attempts = 0;
    const poll = setInterval(function () {
      if (++attempts > MAX_POLL_ATTEMPTS) {
        clearInterval(poll);
        return;
      }
      const el = document.querySelector(STOCKIST_SELECTOR);
      if (!el || !el.shadowRoot) return;
      clearInterval(poll);
      injectStyles(el.shadowRoot);
    }, 50);
  }
  const DAY_NAMES = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  function isDayField(text) {
    const lower = text.toLowerCase();
    return DAY_NAMES.some(function (day) {
      return lower.indexOf(day) === 0;
    });
  }

  /**
   * Groups day-of-week custom fields into a dedicated "Opening Hours" section.
   * Non-day custom fields remain in the original .stockist-result-custom-fields list.
   */
  function groupOpeningHours(inner) {
    const customFieldsList = inner.querySelector(
      '.stockist-result-custom-fields'
    );
    if (!customFieldsList) return;

    const allFields = Array.from(
      customFieldsList.querySelectorAll('.stockist-result-custom-field')
    );
    const dayFields = [];

    allFields.forEach(function (field) {
      const nameEl = field.querySelector('.stockist-result-custom-field-name');
      if (!nameEl) return;
      if (!isDayField(nameEl.textContent.trim())) return;

      dayFields.push(field);
      nameEl.textContent = nameEl.textContent
        .trim()
        .replace(/\s*hours?\s*$/i, '');
    });

    if (dayFields.length === 0) return;

    const section = document.createElement('div');
    section.className = 'stockist-result-opening-hours';

    const heading = document.createElement('div');
    heading.className = 'stockist-result-opening-hours__heading';
    /* Drawn sentence case (store-locator open row, node 16835:433382 raster)
       with no CSS uppercase transform. */
    heading.textContent = 'Opening hours';
    section.appendChild(heading);

    const list = document.createElement('ul');
    list.className = 'stockist-result-opening-hours__list';
    dayFields.forEach(function (field) {
      list.appendChild(field);
    });
    section.appendChild(list);

    customFieldsList.parentNode.insertBefore(
      section,
      customFieldsList.nextSibling
    );

    if (
      customFieldsList.querySelectorAll('.stockist-result-custom-field')
        .length === 0
    ) {
      customFieldsList.style.display = 'none';
    }
  }

  /**
   * Restructures result layout to match the Figma design:
   * - Address row with "Get Directions" link on the right
   * - Phone row with "Call Store" link on the right
   * - Website becomes "View Store" at bottom
   * - Country line hidden
   */
  function restructureLayout(inner) {
    const address = inner.querySelector('.stockist-result-address');
    const directionsWrap = inner.querySelector(
      '.stockist-result-directions-link'
    );

    if (address && directionsWrap) {
      const addressRow = document.createElement('div');
      addressRow.className = 'stockist-result-address-row';
      address.parentNode.insertBefore(addressRow, address);
      addressRow.appendChild(address);

      const dirAnchor = directionsWrap.querySelector('a');
      if (dirAnchor) dirAnchor.textContent = 'Get Directions';
      addressRow.appendChild(directionsWrap);
    }

    const phoneLi = inner.querySelector('.stockist-result-phone');
    if (phoneLi) {
      const phoneLink = phoneLi.querySelector('a');
      if (phoneLink) {
        const phoneRow = document.createElement('div');
        phoneRow.className = 'stockist-result-phone-row';

        const phoneText = document.createElement('span');
        phoneText.className = 'stockist-result-phone-text';
        phoneText.textContent = phoneLink.textContent;

        const callLink = document.createElement('a');
        callLink.className = 'stockist-result-call-link';
        callLink.href = phoneLink.href;
        callLink.textContent = 'Call Store';

        phoneRow.appendChild(phoneText);
        phoneRow.appendChild(callLink);
        phoneLi.innerHTML = '';
        phoneLi.appendChild(phoneRow);
      }
    }

    const websiteLi = inner.querySelector('.stockist-result-website');
    if (websiteLi) websiteLi.style.display = 'none';

    const boutiqueField = Array.from(
      inner.querySelectorAll('.stockist-result-custom-field')
    ).find(function (field) {
      const nameEl = field.querySelector('.stockist-result-custom-field-name');
      return nameEl && nameEl.textContent.trim() === 'Boutique Page Link';
    });

    if (boutiqueField) {
      const boutiqueUrl = boutiqueField.querySelector(
        '.stockist-result-custom-field-text'
      );
      if (boutiqueUrl && boutiqueUrl.textContent.trim()) {
        let href = boutiqueUrl.textContent.trim();
        if (!/^https?:\/\//.test(href) && !href.startsWith('/')) {
          href = 'https://' + href;
        }

        const viewStoreDiv = document.createElement('div');
        viewStoreDiv.className = 'stockist-result-view-store';

        const link = document.createElement('a');
        link.href = href;
        link.target = '_blank';
        link.textContent = 'View Store';
        link.setAttribute(
          'aria-label',
          'View Store (This link will open in a new tab)'
        );
        viewStoreDiv.appendChild(link);
        inner.appendChild(viewStoreDiv);
      }
      boutiqueField.style.display = 'none';
    }

    const country = inner.querySelector('.stockist-result-addr-country');
    if (country) country.style.display = 'none';
  }

  function processResults(container) {
    const results = container.querySelectorAll(
      '.stockist-result:not([data-accordion-init])'
    );

    results.forEach(function (result) {
      result.setAttribute('data-accordion-init', '');

      const name = result.querySelector('.stockist-result-name');
      if (!name) return;

      const body = document.createElement('div');
      body.className = 'stockist-result-accordion-body';

      const inner = document.createElement('div');
      inner.className = 'stockist-result-accordion-body__inner';

      const children = Array.from(result.children);
      children.forEach(function (child) {
        if (child !== name) {
          inner.appendChild(child);
        }
      });

      body.appendChild(inner);
      result.appendChild(body);

      restructureLayout(inner);
      groupOpeningHours(inner);

      name.addEventListener('click', function (e) {
        e.stopPropagation();
        result.classList.toggle('stockist-accordion-open');
      });
    });
  }

  /**
   * Processes map popover results. The CSS grid layout already handles
   * presentation - we only need to point the website link at the
   * boutique page URL (from the custom field) or hide it if absent.
   */
  function processMapResults(shadowRoot) {
    const mapResults = shadowRoot.querySelectorAll(
      '.stockist-map-result:not([data-map-init])'
    );

    mapResults.forEach(function (result) {
      result.setAttribute('data-map-init', '');

      const websiteLi = result.querySelector('.stockist-result-website');
      if (!websiteLi) return;

      const boutiqueField = Array.from(
        result.querySelectorAll('.stockist-result-custom-field')
      ).find(function (field) {
        const nameEl = field.querySelector('.stockist-result-custom-field-name');
        return nameEl && nameEl.textContent.trim() === 'Boutique Page Link';
      });

      if (boutiqueField) {
        const boutiqueUrl = boutiqueField.querySelector(
          '.stockist-result-custom-field-text'
        );
        const boutiqueText = boutiqueUrl && boutiqueUrl.textContent.trim();

        if (boutiqueText) {
          let href = boutiqueText;
          if (!/^https?:\/\//.test(href) && !href.startsWith('/')) {
            href = 'https://' + href;
          }

          const websiteLink = websiteLi.querySelector('a');
          if (websiteLink) {
            websiteLink.href = href;
            websiteLink.setAttribute('aria-label', 'View Store (This link will open in a new tab)');
          }
        } else {
          websiteLi.style.display = 'none';
        }
      } else {
        websiteLi.style.display = 'none';
      }
    });
  }

  /**
   * Reads the region (NSW/VIC/QLD/.../NZ) a result belongs to. The country
   * field is checked first so New Zealand results resolve to NZ -- their
   * state field holds a NZ region name, which would never match a filter
   * button. Otherwise Stockist's own addr-state field is used; if it's
   * absent the full address text is scanned for a state abbreviation as a
   * fallback, since the widget's DOM shape isn't in our control.
   */
  function getResultRegion(result) {
    const countryEl = result.querySelector('.stockist-result-addr-country');
    const countryText = countryEl && countryEl.textContent.trim().toUpperCase();
    if (countryText && NZ_COUNTRY_VALUES.indexOf(countryText) !== -1) return 'NZ';

    const stateEl = result.querySelector('.stockist-result-addr-state');
    const stateText = stateEl && stateEl.textContent.trim();
    if (stateText) return stateText.toUpperCase();

    const addressEl = result.querySelector('.stockist-result-address');
    const addressText = addressEl && addressEl.textContent;
    const match = addressText && addressText.match(STATE_REGEX);
    return match ? match[1].toUpperCase() : null;
  }

  /**
   * Applies the current activeRegion to whatever results are in the DOM
   * right now. Called after every render (initial load and each mutation of
   * the result list) so a filter set before or during a new search sticks.
   */
  function applyRegionFilter() {
    const widget = document.querySelector(STOCKIST_SELECTOR);
    if (!widget || !widget.shadowRoot) return;

    const results = widget.shadowRoot.querySelectorAll(
      '.stockist-result-list .stockist-result'
    );
    results.forEach(function (result) {
      const matches = !activeRegion || getResultRegion(result) === activeRegion;
      result.classList.toggle('sportscraft-store-hidden', !matches);
    });
  }

  /**
   * Runs the widget's own address search so its map recentres/zooms the same
   * way it would for a manual search -- we have no direct handle on the
   * Google Map instance inside the shadow root, only on the search form, so
   * this reuses the widget's existing search behaviour rather than trying to
   * drive the map ourselves. The native value setter + dispatched events are
   * needed in case the widget's input is a framework-controlled component
   * that ignores a plain `.value =` assignment.
   */
  function triggerLocationSearch(query) {
    const widget = document.querySelector(STOCKIST_SELECTOR);
    if (!widget || !widget.shadowRoot) return;

    const input = widget.shadowRoot.querySelector('.stockist-search-field input');
    const button = widget.shadowRoot.querySelector('.stockist-search-button');
    if (!input || !button) return;

    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    ).set;
    nativeSetter.call(input, query);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    button.click();
  }

  function setActiveRegion(region) {
    activeRegion = activeRegion === region ? null : region;

    document.querySelectorAll('[data-sportscraft-store-filter-region]').forEach(
      function (button) {
        const isActive =
          button.getAttribute('data-sportscraft-store-filter-region') === activeRegion;
        button.classList.toggle('sportscraft-tabs__tab--active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      }
    );

    triggerLocationSearch(activeRegion ? REGION_SEARCH_QUERY[activeRegion] : '');
    applyRegionFilter();
  }

  /**
   * The filter menu sits in its own section near the top of the store
   * locator page, well above the widget -- scroll the widget into view on
   * every click so the map/list update is actually visible.
   */
  function scrollToWidget() {
    const widget = document.querySelector(STOCKIST_SELECTOR);
    if (!widget) return;
    widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function bindStoreFilter() {
    document.addEventListener('click', function (e) {
      const button = e.target.closest('[data-sportscraft-store-filter-region]');
      if (!button) return;
      setActiveRegion(button.getAttribute('data-sportscraft-store-filter-region'));
      scrollToWidget();
    });
  }

  function initAccordions(shadowRoot) {
    const resultList = shadowRoot.querySelector('.stockist-result-list');
    if (!resultList) return;

    processResults(resultList);
    applyRegionFilter();

    const listObserver = new MutationObserver(function () {
      processResults(resultList);
      applyRegionFilter();
    });
    listObserver.observe(resultList, { childList: true, subtree: true });

    /* Watch the full shadow root for map popover results (Google Maps
       info windows are injected outside .stockist-result-list). */
    processMapResults(shadowRoot);

    const mapObserver = new MutationObserver(function () {
      processMapResults(shadowRoot);
    });
    mapObserver.observe(shadowRoot, { childList: true, subtree: true });
  }

  function waitForWidget() {
    let attempts = 0;
    const poll = setInterval(function () {
      if (++attempts > MAX_POLL_ATTEMPTS) {
        clearInterval(poll);
        return;
      }

      const el = document.querySelector(STOCKIST_SELECTOR);
      if (!el || !el.shadowRoot) return;

      const list = el.shadowRoot.querySelector('.stockist-result-list');
      if (!list || !list.querySelector('.stockist-result')) return;

      clearInterval(poll);
      injectStyles(el.shadowRoot);
      initAccordions(el.shadowRoot);
      el.classList.add('is-ready');
    }, 300);
  }

  function boot() {
    waitForShadowRoot();
    waitForWidget();
    bindStoreFilter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
